import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckSquare, FilePlus2, Plus } from 'lucide-react';
import { Button, EmptyState, ErrorPanel, KpiCard, PageHeader, PanelCard, Segmented, Spinner, Chip, Track, PillTabs } from '../../school_panel/components/ui';
import { LINK_BTN } from '../../school_panel/components/ui/linkButton';
import { SchoolApi } from '../../school_panel/api/client';
import { useSchoolTasks } from '../../school_panel/api/hooks';
import type { StaffSchoolTask } from '../../school_panel/api/types';
import { useAuth } from '../../utils/Auth/AuthProvider';
import NoticeDetailModal from './components/NoticeDetailModal';
import TaskTrackerDrawer from './components/TaskTrackerDrawer';
import NoticeComposer from './components/NoticeComposer';
import { TaskBoard, TaskTable } from './components/TaskBoard';
import { completionDelta, taskKpis, taskStatus } from './taskStatus';

type Kind = 'all' | 'exam' | 'notice';
type View = 'board' | 'list';

const VIEW_OPTIONS = [{ key: 'board' as const, label: 'Board' }, { key: 'list' as const, label: 'List' }];
const STATE_OPTIONS = [{ key: 'active' as const, label: 'Active' }, { key: 'archived' as const, label: 'Archived' }];
const KIND_OPTIONS: { key: Kind; label: string }[] = [
  { key: 'all', label: 'All' }, { key: 'exam', label: 'Exams' }, { key: 'notice', label: 'Notices' },
];

const FIELD_WRAP = 'flex min-h-[44px] items-center gap-2 rounded-[12px] border border-line-strong bg-surface px-3.5 focus-within:ring-2 focus-within:ring-accent';
const FIELD_INPUT = 'min-w-0 flex-1 border-0 bg-transparent py-2 text-sm text-ink outline-none';

export default function SchoolTasksPage() {
  const { role } = useAuth();
  const isAdmin = role === 'school_admin' || role === 'school' || role === 'admin' || role === 'superuser';
  const [view, setView] = useState<View>('board');
  const [kind, setKind] = useState<Kind>('all');
  const [active, setActive] = useState(true);
  const [grade, setGrade] = useState<number | null>(null);
  const [creator, setCreator] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);
  const [openNotice, setOpenNotice] = useState<StaffSchoolTask | null>(null);
  const [trackerTask, setTrackerTask] = useState<StaffSchoolTask | null>(null);
  const [sent, setSent] = useState(false);

  const gradesQ = useQuery({ queryKey: ['analytics.grades'], queryFn: () => SchoolApi.analyticsGrades() });
  const grades = gradesQ.data?.grades ?? [];

  // A selected grade that is not in the list (deleted or out of scope) counts as All grades.
  const gradeKnown = grade != null && grades.some((g) => g.id === grade);
  const effGrade = grade == null || gradesQ.data == null || gradeKnown ? grade : null;

  const q = useSchoolTasks({
    ...(kind !== 'all' ? { kind } : {}),
    state: active ? 'published' : 'archived',
    ...(effGrade != null ? { grade: effGrade } : {}),
  });
  const seen = new Set<number>();
  const loaded = (q.data?.pages ?? []).flatMap((p) => p.tasks).filter((t) => !seen.has(t.id) && !!seen.add(t.id));
  const more = !!q.hasNextPage;

  // "Created by" narrows the loaded rows only; the server has no such filter.
  const creators = useMemo(() => Array.from(new Set([...loaded.map((t) => t.created_by), ...(creator ? [creator] : [])])).sort((a, b) => a.localeCompare(b)), [loaded, creator]);
  const tasks = creator ? loaded.filter((t) => t.created_by === creator) : loaded;

  const now = new Date();
  const rows = tasks.map((task) => ({ task, status: taskStatus(task, now) }));
  const followUps = rows.filter((r) => r.status === 'follow_up').length;
  const k = taskKpis(tasks);
  const ofLoaded = more ? <p>of the {k.total} loaded</p> : null;
  const delta = completionDelta(tasks, now);

  const subtitle = active
    ? `Exams and notices for your students: ${tasks.length} active, ${followUps} need a follow-up${more ? ', of the loaded tasks' : ''}.`
    : `${tasks.length} archived${more ? ' of the loaded tasks' : ''}`;

  // The kind filter is applied by the server, so only the visible set can be counted honestly.
  const kindCount = (key: Kind): number | null => {
    if (kind === 'all') return key === 'all' ? loaded.length : loaded.filter((t) => t.kind === key).length;
    return key === kind ? loaded.length : null;
  };

  return (
    <>
      <PageHeader eyebrow="Workflow" title="School tasks" subtitle={subtitle}
        actions={
          <>
            <Segmented label="View" options={VIEW_OPTIONS} value={view} onChange={setView} />
            <Link to="/exams/add-exam" className={LINK_BTN}><FilePlus2 size={18} strokeWidth={1.8} aria-hidden="true" />Assign an exam</Link>
            <Button icon={<Plus size={18} strokeWidth={1.8} aria-hidden="true" />} onClick={() => { setSent(false); setComposerOpen(true); }}>New notice</Button>
          </>
        } />
      {sent && <p role="status" className="mb-3 text-sm font-semibold text-success">Notice sent</p>}

      <div className="mb-[18px] grid grid-cols-1 gap-[18px] min-[821px]:grid-cols-2 min-[1181px]:grid-cols-4">
        <KpiCard label={active ? 'Active tasks' : 'Archived tasks'} size="lg" value={k.total}
          sub={<><p>{k.exams} {k.exams === 1 ? 'exam' : 'exams'} · {k.notices} {k.notices === 1 ? 'notice' : 'notices'}</p>{ofLoaded}</>} />
        <KpiCard label="Exam completion" size="lg" value={k.completionPct == null ? '—' : k.completionPct} unit={k.completionPct == null ? undefined : '%'}
          chip={delta != null && delta !== 0 ? <Chip tone={delta > 0 ? 'up' : 'down'}>{delta > 0 ? '↑' : '↓'} {Math.abs(delta)} pts</Chip> : undefined}
          track={<Track className="mt-0" value={k.completionPct ?? 0} tone="slate" label="Exam completion" />}
          sub={ofLoaded} />
        <KpiCard label="Missing work" size="lg" tone={k.missing > 0 ? 'danger' : 'default'} value={k.missing}
          chip={<Chip tone={k.missing > 0 ? 'down' : 'neutral'}>{k.missingTasks} {k.missingTasks === 1 ? 'task' : 'tasks'}</Chip>}
          sub={<><p>students past due, not submitted</p>{ofLoaded}</>} />
        <KpiCard label="Notices read" size="lg" value={k.readPct == null ? '—' : k.readPct} unit={k.readPct == null ? undefined : '%'}
          sub={<><p>{k.noticeAudience > 0 ? `${k.noticeRead} of ${k.noticeAudience} recipients` : 'read by recipients'}</p>{ofLoaded}</>} />
      </div>

      <NoticeComposer isOpen={composerOpen} onClose={() => setComposerOpen(false)} isAdmin={isAdmin} onSent={() => setSent(true)} />

      <div className="mb-[18px]">
        <div className="flex flex-wrap items-center gap-2.5">
          <PillTabs label="Task type" dense height={38} weight="semibold" scroll value={kind} onChange={setKind}
            options={KIND_OPTIONS.map((o) => { const n = more ? null : kindCount(o.key); return { key: o.key, label: o.label, ...(n != null ? { count: n } : {}) }; })} />
          <label className={`${FIELD_WRAP} !min-h-[38px]`}>
            <span className="sr-only">Created by</span>
            <select aria-label="Created by" className={FIELD_INPUT} value={creator} onChange={(e) => setCreator(e.target.value)}>
              <option value="">Anyone</option>
              {creators.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <span className="ml-auto">
            <Segmented label="State" options={STATE_OPTIONS} value={active ? 'active' : 'archived'} onChange={(s) => setActive(s === 'active')} />
          </span>
        </div>
        {grades.length > 0 && (
          <div className="mt-3">
            <PillTabs label="Grade" dense height={38} weight="semibold" scroll value={effGrade == null ? 'all' : String(effGrade)}
              onChange={(k) => setGrade(k === 'all' ? null : Number(k))}
              options={[{ key: 'all', label: 'All grades' }, ...grades.map((g) => ({ key: String(g.id), label: g.name }))]} />
          </div>
        )}
      </div>

      {q.isLoading ? (
        <PanelCard><div className="flex justify-center py-8"><Spinner size="md" /></div></PanelCard>
      ) : q.isError ? (
        <PanelCard><ErrorPanel body="Could not load school tasks. Try again." /></PanelCard>
      ) : tasks.length === 0 ? (
        <PanelCard>
          <EmptyState icon={<CheckSquare size={22} strokeWidth={1.8} />}
            title={loaded.length > 0 ? 'No tasks from this person.' : active ? 'No school tasks yet.' : 'Nothing archived.'}
            body={loaded.length > 0 ? undefined : 'Archived exams and notices stay here so you can still read their results.'} />
          {more && <LoadMore q={q} />}
        </PanelCard>
      ) : view === 'board' ? (
        <>
          <TaskBoard rows={rows} onOpenNotice={setOpenNotice} onOpenTracker={setTrackerTask} />
          {more && <LoadMore q={q} />}
        </>
      ) : (
        <PanelCard title="All tasks" actions={<span className="text-[13px] font-normal text-muted">{tasks.length} shown · newest first</span>}>
          <TaskTable rows={rows} onOpenNotice={setOpenNotice} onOpenTracker={setTrackerTask} />
          {more && <LoadMore q={q} />}
        </PanelCard>
      )}

      <NoticeDetailModal task={openNotice} onClose={() => setOpenNotice(null)} />
      <TaskTrackerDrawer task={trackerTask} onClose={() => setTrackerTask(null)} />
    </>
  );
}

function LoadMore({ q }: { q: { isFetchingNextPage: boolean; fetchNextPage: () => unknown } }) {
  return (
    <div className="flex justify-center pt-4">
      <Button variant="ghost" loading={q.isFetchingNextPage} onClick={() => q.fetchNextPage()}>Load more</Button>
    </div>
  );
}
